import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import routesConfig from "utils/routesConfig";

// IMPORTANT:
// This page is a runtime/demo integration that should not break Jest unit tests.
// We lazy-load the SDK so importing this module (via Routes) doesn't require the SDK in test env.
import type { SDKConfig } from "@brainhealth/video-sessions-sdk";

/**
 * Implementation of the video coaching session using the built SDK
 *
 * This component uses the video-sessions-sdk package, which provides
 * the CoachVideoSession component and SDKConfig type with full TypeScript
 * support via the built type definitions.
 */
export const ZoomUIExample: React.FC = () => {
  const history = useHistory();
  const [
    CoachVideoSession,
    setCoachVideoSession
  ] = useState<React.ComponentType<any> | null>(null);

  useEffect(() => {
    // Avoid importing the SDK in Jest where module resolution may differ (and isn't needed).
    if (process.env.NODE_ENV === "test") return;

    let cancelled = false;

    // Load SDK component + styles at runtime.
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    import("@brainhealth/video-sessions-sdk").then(mod => {
      if (cancelled) return;
      setCoachVideoSession(() => mod.CoachVideoSession as any);
    });

    // CRA v4 (webpack 4) does not reliably respect package "exports" for CSS subpaths.
    // Import the built CSS directly from dist.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    require("@brainhealth/video-sessions-sdk/dist/index.css");

    return () => {
      cancelled = true;
    };
  }, []);

  // Route the user back to the dashboard
  const handleBackToDashboard = () => {
    history.push(routesConfig.dashboard.path);
  };

  // Create SDK config with credentials from environment variables
  const sdkConfig: SDKConfig = useMemo(() => {
    const sdkKey = process.env.REACT_APP_ZOOM_SDK_KEY || "";
    const sdkSecret = process.env.REACT_APP_ZOOM_SDK_SECRET || "";
    const debug = process.env.REACT_APP_VIDEO_SESSIONS_DEBUG === "true";

    if (!sdkKey || !sdkSecret) {
      console.warn(
        "Zoom SDK credentials not found in environment variables. " +
          "Please set REACT_APP_ZOOM_SDK_KEY and REACT_APP_ZOOM_SDK_SECRET"
      );
    }

    return {
      provider: "zoom",
      credentials: {
        zoom: {
          sdkKey,
          sdkSecret
        }
      },
      debug,
      ui: {
        mobileBreakpoint: 900 // Can be customized based on app's breakpoints
      }
    };
  }, []);

  return CoachVideoSession ? (
    <CoachVideoSession
      config={sdkConfig}
      defaultSessionName="Coaching Call"
      defaultUserName="Test User"
      recordingConsent={true}
      sessionTime="July 23th | 2:00 PM - 3:00 PM CDT"
      onBackToDashboard={handleBackToDashboard}
    />
  ) : (
    <div style={{ padding: 24 }}>Loading video session…</div>
  );
};
